import { Key, ProductConfigName } from '@oy/types/src/Config/FrontendConfig';
import { toast } from '@oy/uidesktop';
import numeral from 'numeral';
import React, { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { postAskRefund } from 'src/clients';
import {
  B2xPermissionDetail,
  B2xPermissionProducts,
  INSTANT_WITHDRAWAL_MAX_AMOUNT,
} from 'src/constants';
import { USER_FEEDBACK_WITHDRAWAL } from 'src/constants/UserFeedback/Dashboard/UserFeedback';
import {
  useEnv,
  usePermission,
  useTranslate,
  useWithdrawalInfo,
} from 'src/hooks';
import { useBankData } from 'src/hooks/useBankData';
import { useFrontendConfigs } from 'src/hooks/useFrontendConfig';
import { ACCOUNT_STATEMENT_URL } from 'src/models';
import { useAccountStatementTrack } from 'src/pages/TransactionReport/AccountStatement/hooks';
import { RootState } from 'src/redux/reducers/rootReducer';
import { USER_FEEDBACK_ACTION_TYPES } from 'src/redux/types/userFeedbackTypes';
import { reportFunctionError } from 'src/utilities/sentry';
import { isBetweenJakartaTime } from 'src/utilities/time';
import { mutate } from 'swr';
import { useWithdrawal } from './useWithdrawal';

const OPENING_TIME = '03.00';
const CLOSING_TIME = '20.00';

export const useWithdrawalModalImpl = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { banks } = useBankData();
  const { currentEnv: env } = useEnv();
  const { getTranslation } = useTranslate();
  const { hasPermission } = usePermission();
  const { trackWithdrawal } = useAccountStatementTrack();
  const { data: withdrawalInfo } = useWithdrawalInfo();
  const { maxWithdrawalAmount, withdrawAdminFee, withdrawTax } = withdrawalInfo;

  const [amount, setAmount] = useState(0);
  const [isManual, setIsManual] = useState(false);
  const [showConfirmWithdrawal, setShowConfirmWithdrawal] = useState(false);
  const [isInstantWithdrawalDisable, setIsInstantWithdrawalDisable] =
    useState(false);

  const { isModalOpen: visible, toggleModal: toggleWithdrawalModal } =
    useWithdrawal();
  const receiverInfo = useSelector((state: RootState) => {
    return {
      ...state.main.paymentInfo,
      bank: banks.find(
        ({ value }) => value === state.main.paymentInfo?.receivingBankCode,
      ),
    };
  });

  const showAutoWithdrawalButton = hasPermission(
    B2xPermissionProducts.AUTO_WITHDRAWAL,
    B2xPermissionDetail.VIEWER,
  );

  const { getConfigAsString, isIncludedInList } = useFrontendConfigs();
  const underMaintenance = getConfigAsString(
    ProductConfigName.MANUAL_WITHDRAWAL,
    Key.UNDER_MAINTENANCE,
  );
  const isUnderMaintenance = isIncludedInList(
    receiverInfo.bank?.value ?? '',
    underMaintenance ?? '',
  );

  const isOpeningLessThanClosing = OPENING_TIME < CLOSING_TIME;
  const isNotInService = !isBetweenJakartaTime(OPENING_TIME, CLOSING_TIME);
  const formattedTime = useMemo(() => {
    const firstTime = isOpeningLessThanClosing ? OPENING_TIME : CLOSING_TIME;
    const secondTime = isOpeningLessThanClosing ? CLOSING_TIME : OPENING_TIME;
    return `${firstTime} ${getTranslation('common_to_date')} ${secondTime}`;
  }, [getTranslation, isOpeningLessThanClosing]);

  const adminFee = isManual ? 0 : withdrawAdminFee;
  const tax = isManual ? 0 : withdrawTax;
  const isBalanceNotSufficient = amount + adminFee + tax > maxWithdrawalAmount;
  const isExceedInstantWithdrawalLimit = amount > INSTANT_WITHDRAWAL_MAX_AMOUNT;

  useEffect(() => {
    setIsInstantWithdrawalDisable(
      () =>
        (receiverInfo.bank?.value === '014' && isNotInService) ||
        isExceedInstantWithdrawalLimit,
    );
  }, [
    isExceedInstantWithdrawalLimit,
    isNotInService,
    maxWithdrawalAmount,
    receiverInfo.bank?.value,
  ]);

  useEffect(() => {
    if (isInstantWithdrawalDisable) setIsManual(true);
  }, [isInstantWithdrawalDisable, maxWithdrawalAmount]);

  useEffect(() => {
    if (visible) {
      dispatch({ type: 'SET_WITHDRAWAL', payload: { isSubmitting: false } });
      setAmount(0);
      setIsManual(false);
    }
  }, [visible, dispatch]);

  const getWithdrawalType = () => (isManual ? 'manual' : 'instant');

  const disableSubmit =
    isBalanceNotSufficient || (!isManual && isInstantWithdrawalDisable);

  const errorMessage = isBalanceNotSufficient
    ? getTranslation(
        isManual
          ? 'insufficient_funds'
          : 'refundModal_warningMessage_insufficientBalance',
      )
    : undefined;

  const isWithdrawAll = amount === maxWithdrawalAmount - adminFee - tax;

  const handleChangeAmount = (e: React.ChangeEvent<HTMLInputElement>) => {
    const amount = numeral(e.target.value).value();
    setAmount(amount ?? 0);
  };

  const handleChangeIsManual = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isCurrentManual = e.target.value === 'manual';

    if (isWithdrawAll) {
      setAmount(prev =>
        !isCurrentManual
          ? prev - withdrawAdminFee - withdrawTax
          : prev + withdrawAdminFee + withdrawTax,
      );
    }
    setIsManual(isCurrentManual);
  };

  const askWithdrawal = () => {
    dispatch({ type: 'SET_WITHDRAWAL', payload: { isSubmitting: true } });
    const data = { amount, isManual };

    postAskRefund(data)
      .then(({ data }) => {
        if (data.success)
          toast.success(getTranslation('withdrawal_message_submit'));
        else toast.error(data.reason ?? data.data?.message ?? data.error);
        trackWithdrawal(getWithdrawalType(), env);
        dispatch({ type: 'SET_WITHDRAWAL', payload: { isSubmitting: false } });
        mutate(ACCOUNT_STATEMENT_URL);
        dispatch({
          type: USER_FEEDBACK_ACTION_TYPES.SHOW_USER_FEEDBACK_MODAL,
          payload: USER_FEEDBACK_WITHDRAWAL,
        });
      })
      .catch(exception => {
        dispatch({ type: 'SET_WITHDRAWAL', payload: { isSubmitting: false } });
        reportFunctionError({
          domain: 'dashboard',
          message: 'Failed to submit withdrawal',
          name: 'postAskRefund',
          exception,
          parentName: 'WithdrawalModal',
        });
      });
  };

  const toggleShowConfirmWithdrawal = () =>
    setShowConfirmWithdrawal(prev => !prev);

  const toggleModal = () => {
    toggleWithdrawalModal();
    setShowConfirmWithdrawal(false);
    setAmount(0);
  };

  const handleSubmitWithdrawal = () => {
    askWithdrawal();
    toggleModal();
  };

  const gotoAutoWithdrawal = () => {
    toggleModal();
    navigate('/settings/auto-withdrawal');
  };

  const handleWithdrawAllChange = (e: ChangeEvent<HTMLInputElement>) => {
    let fee = adminFee + tax;
    if (maxWithdrawalAmount > INSTANT_WITHDRAWAL_MAX_AMOUNT) {
      setIsManual(true);
      fee = 0;
    }
    setAmount(e.target.checked ? maxWithdrawalAmount - fee : 0);
  };

  const state = {
    adminFee,
    amount,
    tax,
    receiverInfo,
    disableSubmit,
    errorMessage,
    formattedTime,
    isExceedInstantWithdrawalLimit,
    isInstantWithdrawalDisable,
    isManual,
    isNotInService,
    isOpeningLessThanClosing,
    isUnderMaintenance,
    isWithdrawAll,
    maxWithdrawalAmount,
    showAutoWithdrawalButton,
    showConfirmWithdrawal,
    visible,
  };

  return {
    state,
    getWithdrawalType,
    gotoAutoWithdrawal,
    handleChangeAmount,
    handleChangeIsManual,
    handleSubmitWithdrawal,
    handleWithdrawAllChange,
    toggleShowConfirmWithdrawal,
    toggleModal,
  };
};
